// Notification Sound Utility
export class NotificationSound {
  private static instance: NotificationSound;
  private audioContext: AudioContext | null = null;
  private isEnabled: boolean = true;

  private constructor() {
    // Initialize audio context on first user interaction
    this.initAudioContext();
  }

  public static getInstance(): NotificationSound {
    if (!NotificationSound.instance) {
      NotificationSound.instance = new NotificationSound();
    }
    return NotificationSound.instance;
  }

  private initAudioContext() {
    // Create audio context on first user interaction
    const initAudio = () => {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
    };

    // Initialize on first user interaction
    document.addEventListener('click', initAudio, { once: true });
    document.addEventListener('keydown', initAudio, { once: true });
    document.addEventListener('touchstart', initAudio, { once: true });
  }

  public playMessageSound() {
    if (!this.isEnabled || !this.audioContext) return;
    
    try {
      // Create a pleasant ding sound for messages
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      // Create a two-tone ding sound
      oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
      oscillator.frequency.setValueAtTime(1000, this.audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn('Could not play message sound:', error);
    }
  }

  public playFriendRequestSound() {
    if (!this.isEnabled || !this.audioContext) return;
    
    try {
      // Create a different sound for friend requests
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      // Create a notification chime
      oscillator.frequency.setValueAtTime(600, this.audioContext.currentTime);
      oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(1000, this.audioContext.currentTime + 0.2);
      
      gainNode.gain.setValueAtTime(0.4, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.4);
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.4);
    } catch (error) {
      console.warn('Could not play friend request sound:', error);
    }
  }

  public playMentionSound() {
    if (!this.isEnabled || !this.audioContext) return;

    try {
      // Distinct rising chime for @mentions/tags
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.setValueAtTime(700, this.audioContext.currentTime);
      oscillator.frequency.setValueAtTime(950, this.audioContext.currentTime + 0.08);
      oscillator.frequency.setValueAtTime(1200, this.audioContext.currentTime + 0.16);

      gainNode.gain.setValueAtTime(0.35, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.35);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.35);
    } catch (error) {
      console.warn('Could not play mention sound:', error);
    }
  }

  public playGeneralNotificationSound() {
    if (!this.isEnabled || !this.audioContext) return;
    
    try {
      // Create a simple notification sound
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      // Simple beep
      oscillator.frequency.setValueAtTime(1000, this.audioContext.currentTime);
      
      gainNode.gain.setValueAtTime(0.2, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.2);
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.2);
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  }

  public setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
  }

  public isSoundEnabled(): boolean {
    return this.isEnabled;
  }
}

// Export convenience functions
export const playMessageSound = () => NotificationSound.getInstance().playMessageSound();
export const playFriendRequestSound = () => NotificationSound.getInstance().playFriendRequestSound();
export const playMentionSound = () => NotificationSound.getInstance().playMentionSound();
export const playGeneralNotificationSound = () => NotificationSound.getInstance().playGeneralNotificationSound();
export const setNotificationSoundEnabled = (enabled: boolean) => NotificationSound.getInstance().setEnabled(enabled);
export const isNotificationSoundEnabled = () => NotificationSound.getInstance().isSoundEnabled();
